// ----------------------------------------------------------------------------
// Product Form Web Component: giftCardRecipient <gift-card-recipient>
// ----------------------------------------------------------------------------
// Wires up the native Shopify gift card recipient form (see
// gift-card-recipient.liquid). Shopify reads the line item properties straight
// off the form, so this only has to cover what markup alone can't:
//
//   - `Recipient name` is a single Shopify property, but the form shows the
//     same first/last name pair the Recharge widget did. The two visible
//     inputs are unnamed and are joined into the hidden property here.
//   - `__shopify_offset` carries the customer's timezone offset so `Send on`
//     resolves in their local time rather than the shop's.
//   - `Send on` bounds are rendered from shop time; they're re-applied from the
//     customer's local date so a customer ahead of the shop can't submit a date
//     Shopify considers to be in the past.
//   - A remaining-characters counter for the 200 character message limit.
//
// Shopify also validates the recipient fields server-side on `/cart/add`, but
// that surfaces as a failed add-to-cart after the round trip. The submit guard
// below catches the empty/malformed email first so the customer gets an inline
// message instead. It mirrors product-form--gifting-validation.js: a
// capture-phase listener on the document runs before the theme's own form-level
// handler, so stopping propagation there reliably blocks both ADD TO CART and
// BUY NOW (both are `type="submit"` on the same form).
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './_gift-card-recipient.scss';
import translate from 'scripts/helpers/iamota-translate';

// -------------------------------------
// Config
// -------------------------------------

var CONTEXT_SELECTOR = 'gift-card-recipient';
var EMAIL_SELECTOR = '[data-recipient-email]';

// Pragmatic email shape check — matches the intent of an <input type="email">
// (a single "@" with a dotted domain) without over-constraining valid addresses.
var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var MS_PER_DAY = 86400000;

// Tracks the error message this module is currently showing per form, so we
// only clear the shared error container when it still holds *our* message.
var shownErrorByForm = new WeakMap();

// -------------------------------------
// Helpers
// -------------------------------------

/**
 * The customer's local date as `yyyy-mm-dd`, offset by `dayOffset` days.
 * Built from local date parts rather than toISOString(), which would convert
 * to UTC and can land on the wrong day.
 * @param {Number} dayOffset
 * @returns {String}
 */
function localDateString() {
  var dayOffset = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var date = new Date(Date.now() + dayOffset * MS_PER_DAY);
  var month = "".concat(date.getMonth() + 1).padStart(2, '0');
  var day = "".concat(date.getDate()).padStart(2, '0');
  return "".concat(date.getFullYear(), "-").concat(month, "-").concat(day);
}

/**
 * The gift card product form, if `node` is inside one.
 * @param {EventTarget} node
 * @returns {HTMLFormElement|null}
 */
function getGiftCardForm(node) {
  if (!node || typeof node.closest !== 'function') return null;
  var form = node.closest('form');
  if (!form || !form.querySelector(CONTEXT_SELECTOR)) return null;
  return form;
}

/**
 * The `<iam-product-form>` wrapper for a form (exposes setErrorState/errorContainer).
 * @param {HTMLFormElement} form
 * @returns {HTMLElement|null}
 */
function getIamForm(form) {
  var iamForm = form.closest('iam-product-form');
  return iamForm && typeof iamForm.setErrorState === 'function' ? iamForm : null;
}
function isValidEmail(field) {
  return EMAIL_REGEX.test((field.value || '').trim());
}
function showError(form, emailField) {
  var message = (emailField.value || '').trim() ? translate('Please enter a valid recipient email address.', 'gifting.error.email_invalid') : translate("Please enter the recipient's email so we can send them their gift.", 'gifting.error.email_required');
  var iamForm = getIamForm(form);
  if (!iamForm) return;
  iamForm.setErrorState(message);
  shownErrorByForm.set(form, message);
}
function clearError(form) {
  var message = shownErrorByForm.get(form);
  if (!message) return;
  var iamForm = getIamForm(form);
  // Only clear if the container still holds our message — never clobber an
  // unrelated error that replaced it in the meantime.
  if (iamForm && iamForm.errorContainer && iamForm.errorContainer.textContent.trim() === message.trim()) {
    iamForm.classList.remove('--error');
    iamForm.errorContainer.innerHTML = '';
  }
  shownErrorByForm.delete(form);
}

// -------------------------------------
// Web Component
// -------------------------------------

class giftCardRecipient extends HTMLElement {
  connectedCallback() {
    var _this$messageEl;
    this.nameParts = Array.from(this.querySelectorAll('[data-recipient-name-part]'));
    this.nameField = this.querySelector('[data-recipient-name]');
    this.messageEl = this.querySelector('[data-recipient-message]');
    this.countEl = this.querySelector('[data-recipient-message-count]');
    this.sendOnEl = this.querySelector('[data-recipient-send-on]');
    this.offsetField = this.querySelector('[data-recipient-offset]');
    this.updateMessageCount();
    // Seeds the hidden name from whatever is already in the visible inputs.
    // Browsers restore those on a soft reload / back-navigation but not the
    // hidden field, and autofill can set `.value` without firing `input` —
    // either way the name would otherwise submit blank while the form looks
    // filled in.
    this.refresh();
    this.onNameInput = this.updateRecipientName.bind(this);
    this.onMessageInput = this.updateMessageCount.bind(this);
    this.nameParts.forEach(input => input.addEventListener('input', this.onNameInput));
    (_this$messageEl = this.messageEl) === null || _this$messageEl === void 0 || _this$messageEl.addEventListener('input', this.onMessageInput);
  }
  disconnectedCallback() {
    var _this$messageEl2;
    this.nameParts.forEach(input => input.removeEventListener('input', this.onNameInput));
    (_this$messageEl2 = this.messageEl) === null || _this$messageEl2 === void 0 || _this$messageEl2.removeEventListener('input', this.onMessageInput);
  }

  /**
   * Re-derive everything the browser can invalidate behind our back — called
   * on connect and again at submit time.
   */
  refresh() {
    this.setOffset();
    this.setSendOnBounds();
    this.updateRecipientName();
  }

  /**
   * Join the visible first/last name inputs into the single Shopify property.
   * Left blank when neither is filled — Shopify drops blank properties.
   */
  updateRecipientName() {
    if (!this.nameField) return;
    this.nameField.value = this.nameParts.map(input => (input.value || '').trim()).filter(Boolean).join(' ');
  }

  /**
   * Shopify uses this to resolve `Send on` against the customer's timezone.
   */
  setOffset() {
    if (!this.offsetField) return;
    this.offsetField.value = new Date().getTimezoneOffset();
  }

  /**
   * Re-apply the min/max bounds from the customer's local date. The Liquid
   * values are rendered in shop time, which can be a day behind.
   */
  setSendOnBounds() {
    if (!this.sendOnEl) return;
    var maxDays = parseInt(this.dataset.maxSendDays, 10) || 90;
    var today = localDateString();
    var latest = localDateString(maxDays);
    this.sendOnEl.min = today;
    this.sendOnEl.max = latest;

    // Clamp into range. `min`/`max` are advisory only — JustValidate sets
    // `novalidate` on the form, and a date input still accepts typed values
    // outside its bounds. Without this, an out-of-range date reaches
    // /cart/add.js and comes back as a raw server error the customer can't
    // act on. Covers a value rendered in shop time that is already in the
    // past locally, and a page left open past local midnight.
    if (!this.sendOnEl.value || this.sendOnEl.value < today) {
      this.sendOnEl.value = today;
    } else if (this.sendOnEl.value > latest) {
      this.sendOnEl.value = latest;
    }
  }
  updateMessageCount() {
    if (!this.messageEl || !this.countEl) return;
    this.countEl.textContent = "".concat(this.messageEl.value.length, "/").concat(this.messageEl.maxLength);
  }
}
customElements.define('gift-card-recipient', giftCardRecipient);

// -------------------------------------
// Functionality
// -------------------------------------

// Block add-to-cart / buy-now until a valid recipient email is entered. Capture
// phase runs before the theme's form-level submit handler. Delegated on the
// document so a theme-editor section re-render — which swaps in a fresh <form>
// — reuses these listeners rather than accumulating one per render.
document.addEventListener('submit', event => {
  var form = getGiftCardForm(event.target);
  if (!form) return;

  // Recompute at submit time rather than trusting the `input` listeners: the
  // hidden name field can be stale after a restore/autofill, and the send date
  // bounds are stale on a page left open past local midnight.
  var widget = form.querySelector('gift-card-recipient');
  if (widget && typeof widget.refresh === 'function') widget.refresh();
  var emailField = form.querySelector(EMAIL_SELECTOR);
  if (!emailField) return;
  if (isValidEmail(emailField)) {
    clearError(form);
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  showError(form, emailField);
  emailField.focus();
}, true);

// Clear the error as soon as the recipient email becomes valid.
document.addEventListener('input', event => {
  var target = event.target;
  if (!target || typeof target.matches !== 'function' || !target.matches(EMAIL_SELECTOR)) return;
  var form = getGiftCardForm(target);
  if (form && isValidEmail(target)) clearError(form);
}, true);